import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { Text } from '../../components/index';

import { OpenCallController } from '../../components/Call/CallController';
import { CallMemberCard, MessagesCard } from '../../components/Call/Card';
import Main from '../../components/VideoCall/main';

import socket from '../../socket';

import { alertError, alertSuccess } from '../../redux/stateActions';

import { GET_SESSION_STATUS } from '../../services/patient';

const GroupCall = ({ history }) => {
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));

    const dispatch = useDispatch();
    const { id } = useParams();
    const user = useSelector((state) => state.user);

    useEffect(() => {
        if (!id) {
            history.replace('/home');
        }
    }, [id]);

    /*--------------- Call Control Controller ---------------*/
    const roomInitRef = useRef();

    /*--------------- Get Session ---------------*/
    const [meetData, setMeetData] = useState({});
    const [participants, setParticipants] = useState([]);

    const {
        data: getSessionData,
        error: getSessionError,
        refetch: getSessionRefetch,
    } = useQuery(GET_SESSION_STATUS, {
        variables: {
            id,
        },
        skip: _.isEmpty(user) || !id,
        pollInterval: 300000,
    });

    useEffect(() => {
        if (getSessionError) {
            dispatch(alertError(`Cannot Get Session: ${getSessionError.message}`));
        }

        if (getSessionData && !_.isEmpty(getSessionData.session)) {
            const { actualEndTime, sessionEnd, cancelled, meet } = getSessionData.session;

            // check if video call end || check if session end
            if (cancelled) {
                dispatch(alertError(`Session Cancelled`));
                history.replace(`/provider/session/other-open/${id}`);
                return;
            }
            // check if video call end || check if session end
            if (actualEndTime || sessionEnd) {
                dispatch(alertSuccess(`Session Ended`));
                history.replace(`/provider/session/other-open/${id}`);
                return;
            }

            setParticipants(
                _.concat(
                    meet.staff,
                    meet.patients
                        .filter((patient) => patient.user !== null)
                        .map((patient) => patient),
                    meet.otherStaffs.filter((staff) => staff.user !== null).map((staff) => staff)
                )
            );

            setMeetData(meet);
        }
    }, [getSessionError, getSessionData]);

    /*--------------- Member Window ---------------*/
    const [showMembers, setShowMembers] = useState(false);

    const handleCloseMemberts = () => {
        setShowMembers(false);
    };

    const handleParticipants = () => {
        setShowMembers(!showMembers);
        setShowMessages(false);
    };

    /*--------------- Messages Window ---------------*/
    const [showMessages, setShowMessages] = useState(false);
    const [messagesCount, setMessagesCount] = useState(0);

    const handleCloseMessages = () => {
        setShowMessages(false);
    };

    const handleMessages = () => {
        setShowMessages(!showMessages);
        setShowMembers(false);
    };

    /*--------------- End Call ---------------*/
    const endCallCallback = useCallback(() => {
        socket.emit('providerLeaveOpenCall', {
            sessionId: id,
            staffId: user.staff.id,
        });

        history.replace(`/provider/session/other-open/${id}`);
    }, []);

    // open session socket
    useEffect(() => {
        // fire after first render. for open session
        socket.emit('joinOpenSession', { sessionId: id, staffId: user.staff.id });

        socket.on('joinOpenSession', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });

        socket.on('patientLeaveOpenCall', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });
        socket.on('providerLeaveOpenCall', (data) => {
            if (data.sessionId === id) {
                getSessionRefetch();
            }
        });

        // fire when provider click end call
        socket.on('providerEndOpenCall', (data) => {
            if (data.sessionId === id) {
                roomInitRef.current.clientLeaveRoom(endCallCallback);
            }
        });

        // fire when reconnect after disconnect
        socket.on('connect', () => {
            socket.emit('reconnectOpenCall', {
                sessionId: id,
                staffId: user.staff.id,
            });
        });
    }, []);

    return (
        <div
            style={{
                position: 'relative',
                backgroundColor: '#D2D6E9',
                paddingBottom: mdUp ? 0 : '90px',
                minHeight: '100vh',
            }}
        >
            <div
                id="activity-title"
                style={{
                    padding: '15px',
                    background:
                        'linear-gradient(180deg, #494E6A 1.16%, rgba(55, 60, 89, 0.541667) 49.49%, rgba(210, 214, 233, 0) 98.84%)',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1,
                }}
            >
                <Text $color="white" $ellipsis>{`Activity name - ${meetData?.name}`}</Text>
            </div>
            <div
                id="group-open-call-main-container"
                style={{
                    width: '100vw',
                }}
            >
                {!_.isEmpty(meetData) && (
                    <Main
                        ref={roomInitRef}
                        type="open"
                        isProvider={false}
                        faceDetection={false}
                        useMicCamera={false}
                    />
                )}
            </div>

            <OpenCallController
                mdUp={mdUp}
                onParticipants={handleParticipants}
                participantsCount={participants.length}
                onMessages={handleMessages}
                messagesCount={messagesCount}
                onEndCall={() => {
                    try {
                        roomInitRef.current.clientLeaveRoom(endCallCallback);
                    } catch (error) {
                        endCallCallback();
                    }
                }}
            />

            <CallMemberCard
                open={showMembers}
                participants={participants}
                onClose={handleCloseMemberts}
            />
            {!_.isEmpty(meetData) && (
                <MessagesCard
                    open={showMessages}
                    participants={participants}
                    onClose={handleCloseMessages}
                    sender={user.staff.id}
                    user={user}
                    sessionId={id}
                    messagesCount={messagesCount}
                    setMessagesCount={setMessagesCount}
                />
            )}
        </div>
    );
};

GroupCall.propTypes = {
    history: PropTypes.object,
};

export default GroupCall;
